import { c as _c } from "react/compiler-runtime";
import type { PlaybookVersionNoGraph, VaultKind } from '@onefootprint/request-types/dashboard';
import { Divider, InlineAlert, MonacoProvider } from '@onefootprint/ui';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useStore } from '@/components/playbook-builder';
import { useNodePanel } from '@/components/playbook-builder/components/node-panel/node-panel-context';
import useSidebar from '@/components/playbook-builder/hooks/use-sidebar';
import { buildPrerequisiteTemplate } from '@/components/playbook-builder/utils/prerequisite-template';
import useOrgSession from '@/hooks/use-org-session';
import { DEFAULT_OB_DATA, TEST_MODE_NODE_ID } from '../../test-mode.constants';
import { generateTestExternalId } from '../test-form';
import { TestModeButton } from '../test-mode-button';
import { TestPanelHeader } from '../test-panel-header';
import { EntityCreatorSection } from './components/entity-creator-section';
import { RunActions } from './components/run-actions';
import { RunCurlPreview } from './components/run-curl-preview';
import { RunForm } from './components/run-form';
import type { OnboardingSection, RunFormValues } from './onboarding.types';
import { parseJson5Object, runsVerifiedPrefill } from './onboarding.utils';
type OnboardingTestModeProps = {
  selectedVersion: PlaybookVersionNoGraph;
  hasPendingChanges: boolean;
  hasErrors: boolean;
  hasHiddenErrors: boolean;
  onRevealHiddenErrors: () => void;
  // The ID of the in-progress draft version, run in place of the versioned key when there are
  // pending changes so test mode exercises the unsaved edits rather than the latest saved version.
  versionId?: string;
};
export const OnboardingTestMode = t0 => {
  const $ = _c(107);
  const {
    selectedVersion,
    hasPendingChanges,
    hasErrors,
    hasHiddenErrors,
    onRevealHiddenErrors,
    versionId
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "testMode"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("playbook-builder", t1);
  const {
    open: openSidebar,
    close: closeSidebar,
    isOpen: isSidebarOpen
  } = useSidebar();
  const {
    setDescriptor,
    bodySlot,
    footerSlot
  } = useNodePanel();
  let t2;
  if ($[1] !== isSidebarOpen) {
    t2 = isSidebarOpen(TEST_MODE_NODE_ID);
    $[1] = isSidebarOpen;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const isOpen = t2;
  const {
    data: t3
  } = useStore();
  const {
    graph
  } = t3;
  const [savedEntity, setSavedEntity] = useState(null);
  const [activeSection, setActiveSection] = useState("entity-creator");
  const entityKind = selectedVersion.kind === "kyb" ? "business" : "person";
  const {
    sandbox
  } = useOrgSession();
  const savedFpId = savedEntity?.isSandbox === sandbox.isSandbox ? savedEntity.fpId : null;
  let t4;
  if ($[3] !== graph.nodes) {
    t4 = graph.nodes.some(_temp);
    $[3] = graph.nodes;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const hasPrerequisiteNode = t4;
  let prerequisites;
  let showVerifiedMatchTip;
  let t5;
  let t6;
  let t7;
  if ($[5] !== entityKind || $[6] !== graph || $[7] !== sandbox.isSandbox) {
    prerequisites = graph.nodes.flatMap(_temp2);
    showVerifiedMatchTip = entityKind === "person" && sandbox.isSandbox && runsVerifiedPrefill(graph);
    t7 = useForm;
    if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = generateTestExternalId();
      $[13] = t5;
    } else {
      t5 = $[13];
    }
    t6 = buildPrerequisiteTemplate(prerequisites) ?? DEFAULT_OB_DATA;
    $[5] = entityKind;
    $[6] = graph;
    $[7] = sandbox.isSandbox;
    $[8] = prerequisites;
    $[9] = showVerifiedMatchTip;
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
  } else {
    prerequisites = $[8];
    showVerifiedMatchTip = $[9];
    t5 = $[10];
    t6 = $[11];
    t7 = $[12];
  }
  let t8;
  if ($[14] !== t5 || $[15] !== t6) {
    t8 = {
      defaultValues: {
        onboardingExternalId: t5,
        prerequisiteData: t6,
        synchronousTimeoutSecs: 30
      }
    };
    $[14] = t5;
    $[15] = t6;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const form = t7(t8);
  let t9;
  if ($[17] !== form || $[18] !== hasPrerequisiteNode || $[19] !== prerequisites) {
    t9 = () => {
      if (!hasPrerequisiteNode) {
        return;
      }
      const current = form.getValues("prerequisiteData");
      let existingValues;
      try {
        existingValues = parseJson5Object(current);
      } catch {
        return;
      }
      const next = buildPrerequisiteTemplate(prerequisites, existingValues) ?? DEFAULT_OB_DATA;
      if (next !== current) {
        form.setValue("prerequisiteData", next);
      }
    };
    $[17] = form;
    $[18] = hasPrerequisiteNode;
    $[19] = prerequisites;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  const t10 = graph?.id;
  let t11;
  if ($[21] !== t10) {
    t11 = [t10];
    $[21] = t10;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  useEffect(t9, t11);
  let t12;
  if ($[23] !== closeSidebar || $[24] !== isOpen || $[25] !== openSidebar) {
    t12 = () => {
      if (isOpen) {
        closeSidebar();
      } else {
        openSidebar(TEST_MODE_NODE_ID);
      }
    };
    $[23] = closeSidebar;
    $[24] = isOpen;
    $[25] = openSidebar;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  const toggle = t12;
  let t13;
  if ($[27] !== t) {
    t13 = t(_temp3);
    $[27] = t;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== entityKind || $[30] !== t) {
    t14 = entityKind === "business" ? t(_temp4) : t(_temp5);
    $[29] = entityKind;
    $[30] = t;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== t13 || $[33] !== t14) {
    t15 = <TestPanelHeader title={t13} description={t14} docHref="https://docs.onefootprint.com/api-reference#post-onboardings" />;
    $[32] = t13;
    $[33] = t14;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== entityKind || $[36] !== hasPendingChanges || $[37] !== sandbox.isSandbox || $[38] !== t) {
    t16 = (!sandbox.isSandbox || hasPendingChanges) && <div className="flex flex-col gap-3 px-4 pt-4">{!sandbox.isSandbox && <InlineAlert variant="warning">{t($_2 => $_2.runMode.productionAlert[entityKind])}</InlineAlert>}{hasPendingChanges && <InlineAlert variant="neutral">{t(_temp6)}</InlineAlert>}</div>;
    $[35] = entityKind;
    $[36] = hasPendingChanges;
    $[37] = sandbox.isSandbox;
    $[38] = t;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  let t17;
  if ($[40] !== sandbox.isSandbox) {
    t17 = fpId => setSavedEntity({
      fpId,
      isSandbox: sandbox.isSandbox
    });
    $[40] = sandbox.isSandbox;
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  const t18 = activeSection === "entity-creator";
  let t19;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = editing => setActiveSection(editing ? "entity-creator" : "run-form");
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  let t20;
  if ($[43] !== entityKind || $[44] !== savedFpId || $[45] !== showVerifiedMatchTip || $[46] !== t17 || $[47] !== t18) {
    t20 = <EntityCreatorSection entityKind={entityKind} savedFpId={savedFpId} setSavedFpId={t17} isEditing={t18} setIsEditing={t19} showVerifiedMatchTip={showVerifiedMatchTip} />;
    $[43] = entityKind;
    $[44] = savedFpId;
    $[45] = showVerifiedMatchTip;
    $[46] = t17;
    $[47] = t18;
    $[48] = t20;
  } else {
    t20 = $[48];
  }
  const t21 = !!savedFpId && activeSection === "run-form";
  const t22 = !!savedFpId;
  let t23;
  if ($[49] !== entityKind || $[50] !== graph || $[51] !== hasPrerequisiteNode || $[52] !== selectedVersion || $[53] !== t21 || $[54] !== t22) {
    t23 = <RunForm isExpanded={t21} hasSavedEntity={t22} hasPrerequisiteNode={hasPrerequisiteNode} entityKind={entityKind} playbook={selectedVersion} graph={graph} />;
    $[49] = entityKind;
    $[50] = graph;
    $[51] = hasPrerequisiteNode;
    $[52] = selectedVersion;
    $[53] = t21;
    $[54] = t22;
    $[55] = t23;
  } else {
    t23 = $[55];
  }
  let t24;
  if ($[56] !== t20 || $[57] !== t23) {
    t24 = <MonacoProvider languageName="template"><div className="flex flex-col gap-4 px-4 my-4">{t20}{t23}</div></MonacoProvider>;
    $[56] = t20;
    $[57] = t23;
    $[58] = t24;
  } else {
    t24 = $[58];
  }
  let t25;
  if ($[59] !== t15 || $[60] !== t16 || $[61] !== t24) {
    t25 = <>{t15}{t16}{t24}</>;
    $[59] = t15;
    $[60] = t16;
    $[61] = t24;
    $[62] = t25;
  } else {
    t25 = $[62];
  }
  const body = t25;
  let t26;
  if ($[63] !== hasPendingChanges || $[64] !== savedFpId || $[65] !== selectedVersion || $[66] !== versionId) {
    t26 = <RunCurlPreview savedFpId={savedFpId} playbook={selectedVersion} hasPendingChanges={hasPendingChanges} versionId={versionId} />;
    $[63] = hasPendingChanges;
    $[64] = savedFpId;
    $[65] = selectedVersion;
    $[66] = versionId;
    $[67] = t26;
  } else {
    t26 = $[67];
  }
  let t27;
  if ($[68] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <Divider className="-mx-3 !w-[calc(100%+1.5rem)]" />;
    $[68] = t27;
  } else {
    t27 = $[68];
  }
  let t28;
  if ($[69] !== closeSidebar) {
    t28 = () => closeSidebar();
    $[69] = closeSidebar;
    $[70] = t28;
  } else {
    t28 = $[70];
  }
  let t29;
  if ($[71] !== entityKind || $[72] !== hasErrors || $[73] !== hasHiddenErrors || $[74] !== hasPendingChanges || $[75] !== onRevealHiddenErrors || $[76] !== savedFpId || $[77] !== selectedVersion || $[78] !== t28 || $[79] !== versionId) {
    t29 = <RunActions savedFpId={savedFpId} entityKind={entityKind} playbook={selectedVersion} hasErrors={hasErrors} hasHiddenErrors={hasHiddenErrors} onRevealHiddenErrors={onRevealHiddenErrors} hasPendingChanges={hasPendingChanges} versionId={versionId} onClose={t28} />;
    $[71] = entityKind;
    $[72] = hasErrors;
    $[73] = hasHiddenErrors;
    $[74] = hasPendingChanges;
    $[75] = onRevealHiddenErrors;
    $[76] = savedFpId;
    $[77] = selectedVersion;
    $[78] = t28;
    $[79] = versionId;
    $[80] = t29;
  } else {
    t29 = $[80];
  }
  let t30;
  if ($[81] !== t26 || $[82] !== t29) {
    t30 = <div className="flex flex-col gap-4 w-full max-md:min-w-0 max-md:max-h-[55dvh] max-md:overflow-y-auto max-md:-mx-3 max-md:w-[calc(100%+1.5rem)] max-md:px-3">{t26}{t27}{t29}</div>;
    $[81] = t26;
    $[82] = t29;
    $[83] = t30;
  } else {
    t30 = $[83];
  }
  const footer = t30;
  let t31;
  let t32;
  if ($[84] !== closeSidebar || $[85] !== isOpen || $[86] !== setDescriptor) {
    t31 = () => {
      if (!isOpen) {
        return;
      }
      setDescriptor({
        nodeId: TEST_MODE_NODE_ID,
        canEdit: true,
        hasCustomFooter: true,
        size: "lg",
        anchorless: true,
        isDirty: false,
        clearDirty: _temp7,
        onClose: closeSidebar
      });
      return () => setDescriptor(_temp8);
    };
    t32 = [isOpen, setDescriptor, closeSidebar];
    $[84] = closeSidebar;
    $[85] = isOpen;
    $[86] = setDescriptor;
    $[87] = t31;
    $[88] = t32;
  } else {
    t31 = $[87];
    t32 = $[88];
  }
  useEffect(t31, t32);
  let t33;
  if ($[89] !== t) {
    t33 = t(_temp9);
    $[89] = t;
    $[90] = t33;
  } else {
    t33 = $[90];
  }
  let t34;
  if ($[91] !== t33 || $[92] !== toggle) {
    t34 = <TestModeButton label={t33} onClick={toggle} />;
    $[91] = t33;
    $[92] = toggle;
    $[93] = t34;
  } else {
    t34 = $[93];
  }
  let t35;
  if ($[94] !== body || $[95] !== bodySlot || $[96] !== isOpen) {
    t35 = isOpen && bodySlot ? createPortal(body, bodySlot) : null;
    $[94] = body;
    $[95] = bodySlot;
    $[96] = isOpen;
    $[97] = t35;
  } else {
    t35 = $[97];
  }
  let t36;
  if ($[98] !== footer || $[99] !== footerSlot || $[100] !== isOpen) {
    t36 = isOpen && footerSlot ? createPortal(footer, footerSlot) : null;
    $[98] = footer;
    $[99] = footerSlot;
    $[100] = isOpen;
    $[101] = t36;
  } else {
    t36 = $[101];
  }
  let t37;
  if ($[102] !== form || $[103] !== t34 || $[104] !== t35 || $[105] !== t36) {
    t37 = <FormProvider {...form}>{t34}{t35}{t36}</FormProvider>;
    $[102] = form;
    $[103] = t34;
    $[104] = t35;
    $[105] = t36;
    $[106] = t37;
  } else {
    t37 = $[106];
  }
  return t37;
};
function _temp(node) {
  return node.kind === "prerequisite";
}
function _temp2(node_0) {
  return node_0.kind === "prerequisite" ? node_0.prerequisites : [];
}
function _temp3($) {
  return $.runMode.title;
}
function _temp4($_0) {
  return $_0.runMode.businessDescription;
}
function _temp5($_1) {
  return $_1.runMode.description;
}
function _temp6($_3) {
  return $_3.runMode.pendingChangesAlert;
}
function _temp7() {}
function _temp8(current_0) {
  return current_0?.nodeId === TEST_MODE_NODE_ID ? null : current_0;
}
function _temp9($_4) {
  return $_4.title;
}